<script>
  import { createEventDispatcher } from "svelte";
  import { Geocoder } from "@beyonk/svelte-mapbox";
  import { Button } from "carbon-components-svelte";
  import ChevronLeft from "carbon-icons-svelte/lib/ChevronLeft.svelte";

  import { yards, routeSources, loadAddressText } from "../../../stores";
  import { mapDirections } from "../distance";
  import { isFullAddress, validateFullAddress } from "../../../helpers";
  import WarningFilled from "carbon-icons-svelte/lib/WarningFilled.svelte";
  import CheckmarkFilled from "carbon-icons-svelte/lib/CheckmarkFilled.svelte";

  import FilterYard from "./FilterYard.svelte";
  import CompactYardList from "./CompactYardList.svelte";

  export let map;

  let defaultAddress = "";
  let disableMapRouteButton = true;
  let accurateRoutes = 0;
  let addressEvent = null;
  let isMapping = false;
  let addressWarning = "";
  let addressValidation = null;

  const dispatch = createEventDispatcher();

  function onAddressChange(e) {
    addressEvent = e;
    const result = e.detail?.result || e.detail?.detail?.result;
    const placeName = result?.place_name || "";
    const isAddressFeature = result?.place_type?.includes("address");
    const validation = validateFullAddress(placeName);

    if (isAddressFeature || validation.valid) {
      addressValidation = { valid: true };
      addressWarning = "";
      disableMapRouteButton = false;
      dispatch("address-change", e);
      if ($yards && $yards.length > 0) {
        accurateRoutes = $yards.length;
      }
    } else {
      addressValidation = { valid: false };
      addressWarning = "Full address required: MUST include street and suburb (e.g. Church St, Parramatta). Street number is optional.";
      disableMapRouteButton = true;
      dispatch("address-change", e);
    }
  }

  function removeAddress() {
    addressEvent = null;
    addressValidation = null;
    addressWarning = "";
    dispatch("clear-address");
    disableMapRouteButton = true;
    accurateRoutes = 0;
    dispatch("routes-cleared");
  }

  function onYardsListUpdated() {
    if ($yards && $yards.length > 0) {
      accurateRoutes = $yards.length;
    }
    if (addressEvent) {
      disableMapRouteButton = false;
    }
    dispatch("routes-cleared");
    dispatch("yards-list-updated");
  }

  /**
   * Directly fetches and draws actual driving routes for the filtered yards using Mapbox Directions API,
   * showing travel times placed on the route 10% from the destination yards.
   * Dispatches 'routes-displayed' once all driving routes have appeared on the map.
   */
  async function callMapDirections() {
    if (addressEvent) {
      dispatch("address-change", addressEvent);
    }
    isMapping = true;
    disableMapRouteButton = true;

    try {
      const count = await mapDirections(map);
      accurateRoutes = count || ($routeSources ? $routeSources.length : 0);
      dispatch("routes-displayed", { count: accurateRoutes });
    } catch (err) {
      console.error("Error mapping directions:", err);
      disableMapRouteButton = false;
    } finally {
      isMapping = false;
    }
  }

  function hideThisWindow() {
    dispatch("hideLoadAddressWindow", true);
  }
</script>

<div class="quote-box">
  <div class="card-header">
    <div class="header-info">
      <h2 class="card-title">Quote & Route Planner</h2>
      <span class="card-subtitle"
        >Find closest disposal yards & optimize haulage</span
      >
    </div>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <button
      class="close-card-btn"
      on:click={hideThisWindow}
      title="Collapse planner"
      type="button"
    >
      <ChevronLeft size={20} />
    </button>
  </div>

  <div class="card-body">
    <div class="section-block">
      <div class="field-header-row">
        <label class="bx--label mb-0" for="load-address-geo">Load Site Location</label>
        <span class="address-req-tag">Street &amp; Suburb Required</span>
      </div>
      <div id="load-address-geo" class="geocoder-wrapper">
        <Geocoder
          accessToken={MAPBOX_TOKEN}
          on:result={(e) => onAddressChange(e)}
          on:clear={removeAddress}
          value={defaultAddress || $loadAddressText}
          geocoder
          options={{
            countries: "AU",
            types: "address",
            placeholder: "Search site address (e.g. Church St, Parramatta)...",
          }}
        />
      </div>
      {#if addressWarning}
        <div class="address-geo-warning">
          <WarningFilled size={14} />
          <span>{addressWarning}</span>
        </div>
      {:else if addressValidation && addressValidation.valid}
        <div class="address-geo-success">
          <CheckmarkFilled size={14} />
          <span>Full address verified (street and suburb present)</span>
        </div>
      {/if}
    </div>

    <div class="section-block">
      <FilterYard on:filter-yards {map} on:yards-list-updated={onYardsListUpdated}>
        <div slot="action" class="map-route-action">
          <Button
            disabled={disableMapRouteButton || isMapping}
            on:click={callMapDirections}
            size="field"
            kind="secondary"
          >
            {isMapping ? "Mapping Routes..." : "Map Routes"}
          </Button>
        </div>
      </FilterYard>
    </div>

    <div class="section-block yards-list-block">
      <CompactYardList />
    </div>
  </div>
</div>

<style>
  .quote-box {
    position: absolute;
    top: 4rem;
    left: 1rem;
    width: 580px;
    max-width: calc(100vw - 2rem);
    max-height: calc(100% - 5rem);
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(16px);
    border: 1px solid var(--tt-slate-200);
    border-radius: var(--tt-radius-lg);
    box-shadow: var(--tt-shadow-xl);
    z-index: 1200;
    pointer-events: auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: fadeIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.875rem 1.125rem;
    border-bottom: 1px solid var(--tt-slate-200);
    background-color: var(--tt-slate-50);
  }

  .card-title {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 700;
    color: var(--tt-slate-900);
    letter-spacing: -0.01em;
  }

  .card-subtitle {
    font-size: 0.75rem;
    color: var(--tt-slate-500);
  }

  .close-card-btn {
    width: 32px;
    height: 32px;
    border-radius: var(--tt-radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    color: var(--tt-slate-500);
    cursor: pointer;
    transition: all 0.15s ease;
    margin: 0;
    padding: 0;
  }

  .close-card-btn:hover {
    background: var(--tt-slate-200);
    color: var(--tt-slate-800);
  }

  .card-body {
    padding: 1rem 1.125rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .section-block {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .geocoder-wrapper {
    width: 100%;
  }

  .map-route-action {
    width: 100%;
    display: flex;
    align-items: flex-end;
  }

  :global(.map-route-action .bx--btn) {
    width: 100% !important;
    justify-content: center !important;
    border-radius: var(--tt-radius-sm) !important;
    min-height: 40px !important;
    height: 40px !important;
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  .field-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 4px;
  }

  :global(.field-header-row .bx--label.mb-0) {
    margin-bottom: 0 !important;
  }

  .address-req-tag {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 6px;
    border-radius: 4px;
    background: #eff6ff;
    color: #2563eb;
    border: 1px solid #bfdbfe;
  }

  .address-geo-warning {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
    font-weight: 500;
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
    padding: 6px 10px;
    border-radius: 6px;
    margin-top: 4px;
    line-height: 1.3;
  }

  .address-geo-success {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72rem;
    font-weight: 600;
    color: #15803d;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    padding: 4px 8px;
    border-radius: 6px;
    margin-top: 4px;
  }
</style>
